// Unit conversion tables. Every category converts through a base unit, except
// temperature, which needs offsets and therefore carries explicit functions.
// Unit labels are symbols on purpose — they read the same in all 12 locales,
// so only the category names live in the translation files.

export const UNIT_CATEGORIES = {
  length: {
    base: 'm',
    units: {
      mm: 0.001,
      cm: 0.01,
      m: 1,
      km: 1000,
      in: 0.0254,
      ft: 0.3048,
      yd: 0.9144,
      mi: 1609.344,
    },
    defaults: ['km', 'mi'],
  },
  mass: {
    base: 'kg',
    units: {
      mg: 0.000001,
      g: 0.001,
      kg: 1,
      t: 1000,
      oz: 0.028349523125,
      lb: 0.45359237,
    },
    defaults: ['kg', 'lb'],
  },
  temperature: {
    base: '°C',
    units: {
      '°C': { toBase: (v) => v, fromBase: (v) => v },
      '°F': { toBase: (v) => (v - 32) * (5 / 9), fromBase: (v) => v * (9 / 5) + 32 },
      K: { toBase: (v) => v - 273.15, fromBase: (v) => v + 273.15 },
    },
    defaults: ['°C', '°F'],
  },
  area: {
    base: 'm²',
    units: {
      'cm²': 0.0001,
      'm²': 1,
      'km²': 1000000,
      ha: 10000,
      sotix: 100, // Uzbek land measure: one sotix is 100 m².
      'ft²': 0.09290304,
      acre: 4046.8564224,
    },
    defaults: ['sotix', 'm²'],
  },
  volume: {
    base: 'l',
    units: {
      ml: 0.001,
      l: 1,
      'm³': 1000,
      'gal (US)': 3.785411784,
      'fl oz (US)': 0.0295735295625,
    },
    defaults: ['l', 'gal (US)'],
  },
  speed: {
    base: 'm/s',
    units: {
      'm/s': 1,
      'km/h': 1 / 3.6,
      mph: 0.44704,
      knot: 0.514444,
    },
    defaults: ['km/h', 'mph'],
  },
  data: {
    base: 'B',
    units: {
      B: 1,
      KB: 1024,
      MB: 1024 ** 2,
      GB: 1024 ** 3,
      TB: 1024 ** 4,
    },
    defaults: ['MB', 'GB'],
  },
  time: {
    base: 's',
    units: {
      ms: 0.001,
      s: 1,
      min: 60,
      h: 3600,
      d: 86400,
      wk: 604800,
    },
    defaults: ['h', 'min'],
  },
};

export const categoryKeys = Object.keys(UNIT_CATEGORIES);

export const unitsOf = (category) => Object.keys(UNIT_CATEGORIES[category].units);

/** Converts between two units of the same category. Returns NaN for bad input. */
export const convertUnit = (value, from, to, category) => {
  const table = UNIT_CATEGORIES[category];
  if (!table || !Number.isFinite(value)) return NaN;

  const source = table.units[from];
  const target = table.units[to];
  if (source === undefined || target === undefined) return NaN;

  if (typeof source === 'object') {
    return target.fromBase(source.toBase(value));
  }
  return (value * source) / target;
};

/** Formats a converted value without dragging along float noise. */
export const formatUnitValue = (value, locale = 'en') => {
  if (!Number.isFinite(value)) return '—';
  const magnitude = Math.abs(value);
  if (magnitude !== 0 && (magnitude >= 1e12 || magnitude < 1e-6)) {
    return value.toExponential(4);
  }
  const decimals = magnitude >= 100 ? 2 : magnitude >= 1 ? 4 : 6;
  return Number(value.toFixed(decimals)).toLocaleString(locale, { maximumFractionDigits: decimals });
};
